import React, { useState, useMemo } from 'react';
import styles from './Table.module.css';

const Table = ({ columns, data, onRowClick }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const handleSort = (column) => {
    // Only sort if column has accessor or sortKey
    const sortKey = column.sortKey || column.accessor;
    if (!sortKey) return;

    let direction = 'asc';
    if (sortConfig.key === sortKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key: sortKey, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
      const column = columns.find(col => (col.sortKey || col.accessor) === sortConfig.key);
      
      let aValue = column.sortValue ? column.sortValue(a) : a[sortConfig.key];
      let bValue = column.sortValue ? column.sortValue(b) : b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Convert to string for comparison if not numbers
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [data, sortConfig, columns]);

  const getSortIcon = (column) => {
    const sortKey = column.sortKey || column.accessor;
    if (!sortKey) return null;

    if (sortConfig.key === sortKey) {
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return ' ⬍';
  };

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((column, index) => {
              const sortKey = column.sortKey || column.accessor;
              const isSortable = sortKey && column.sortable !== false;
              
              return (
                <th 
                  key={index} 
                  className={`${styles.th} ${isSortable ? styles.sortable : ''}`}
                  onClick={() => isSortable && handleSort(column)}
                >
                  {column.header}
                  {isSortable && (
                    <span className={styles.sortIcon}>
                      {getSortIcon(column)}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={styles.noData}>
                No data available
              </td>
            </tr>
          ) : (
            sortedData.map((row, rowIndex) => (
              <tr 
                key={rowIndex} 
                className={onRowClick ? styles.clickable : ''}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((column, colIndex) => (
                  <td key={colIndex} className={styles.td}>
                    {column.render ? column.render(row) : row[column.accessor]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;